/* Custom dropdown for the shared filter card (ls-filters.css): every `.lt-filters .lt-field select`
   gets a select-only combobox + listbox styled like the collection filter popovers. The native select
   stays in the DOM (hidden) as the source of truth: picking an option sets its value and fires `change`,
   so each page's own filter code is untouched. Touch devices keep the native picker. */
(() => {
  if (window.LsSelect) return;
  const SEL = '.lt-filters .lt-field select';
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
  let uid = 0;
  let open = null;
  let forced = false;

  const split = (text) => {
    const m = /^(.*?)\s*\((\d[\d,]*)\)\s*$/.exec(text);
    return m ? [m[1], m[2]] : [text, ''];
  };

  function enhance(sel) {
    if (sel.dataset.lsSelect) return;
    sel.dataset.lsSelect = '1';
    const id = 'ls-sel-' + (++uid);
    const field = sel.closest('.lt-field');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ls-sel';
    btn.setAttribute('role', 'combobox');
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', id);
    const labelEl = field && field.querySelector('.lt-field-label');
    if (labelEl) {
      if (!labelEl.id) labelEl.id = id + '-label';
      btn.setAttribute('aria-labelledby', labelEl.id + ' ' + id + '-value');
    }
    btn.innerHTML = '<span class="ls-sel__value" id="' + id + '-value"></span>';
    const list = document.createElement('ul');
    list.className = 'ls-sel__list';
    list.id = id;
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    if (labelEl) list.setAttribute('aria-labelledby', labelEl.id);
    sel.classList.add('ls-sel-native');
    sel.tabIndex = -1;
    sel.setAttribute('aria-hidden', 'true');
    sel.before(btn);
    btn.after(list);
    if (field) field.classList.add('has-ls-sel');

    const st = { sel, btn, list, field, active: -1, typed: '', typedAt: 0 };
    sel._lsSel = st;
    build(st);
    new MutationObserver(() => build(st)).observe(sel, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'hidden', 'label'] });

    btn.addEventListener('click', (e) => { e.preventDefault(); open === st ? close(st) : show(st); });
    btn.addEventListener('keydown', (e) => keys(st, e));
    list.addEventListener('mousedown', (e) => { if (e.target.closest('[role=option]')) e.preventDefault(); });
    list.addEventListener('click', (e) => {
      e.preventDefault();
      const li = e.target.closest('[role=option]');
      if (!li || li.getAttribute('aria-disabled') === 'true') return;
      pick(st, +li.dataset.i);
      close(st);
      btn.focus({ preventScroll: true });
    });
    list.addEventListener('mousemove', (e) => {
      const li = e.target.closest('[role=option]');
      if (li && +li.dataset.i !== st.active) setActive(st, +li.dataset.i, false);
    });
  }

  function build(st) {
    const opts = [...st.sel.options];
    st.list.innerHTML = opts.map((o, i) => {
      const [label, count] = split(o.textContent.trim());
      const off = o.disabled || o.hidden;
      return '<li role="option" id="' + st.list.id + '-' + i + '" data-i="' + i + '" class="ls-sel__opt' + (o.hidden ? ' is-hidden' : '') + '" aria-selected="false"' + (off ? ' aria-disabled="true"' : '') + '>' +
        '<span class="ls-sel__label"></span>' + (count ? '<span class="ls-sel__count">' + count + '</span>' : '') + '<span class="ls-sel__check">' + CHECK + '</span></li>';
    }).join('');
    st.list.querySelectorAll('.ls-sel__label').forEach((el, i) => { el.textContent = split(opts[i].textContent.trim())[0]; });
    st.btn.disabled = st.sel.disabled;
    sync(st);
  }

  function sync(st) {
    const i = st.sel.selectedIndex;
    const o = st.sel.options[i];
    st.btn.querySelector('.ls-sel__value').textContent = o ? split(o.textContent.trim())[0] : '';
    st.list.querySelectorAll('[role=option]').forEach((li) => li.setAttribute('aria-selected', String(+li.dataset.i === i)));
  }

  function enabled(st) {
    return [...st.list.querySelectorAll('[role=option]:not([aria-disabled=true])')].map((li) => +li.dataset.i);
  }

  function setActive(st, i, scroll = true) {
    st.active = i;
    st.list.querySelectorAll('[role=option]').forEach((li) => li.classList.toggle('is-active', +li.dataset.i === i));
    const li = st.list.querySelector('[data-i="' + i + '"]');
    if (li) {
      st.btn.setAttribute('aria-activedescendant', li.id);
      if (scroll) li.scrollIntoView({ block: 'nearest' });
    } else st.btn.removeAttribute('aria-activedescendant');
  }

  function place(st) {
    const list = st.list;
    list.removeAttribute('data-up');
    list.removeAttribute('data-align');
    const r = (st.field || st.btn).getBoundingClientRect();
    const h = Math.min(list.scrollHeight, 340);
    const below = innerHeight - r.bottom;
    if (below < h + 16 && r.top > below) list.dataset.up = '';
    const w = list.getBoundingClientRect().width;
    if (r.left + w > document.documentElement.clientWidth - 12) list.dataset.align = 'end';
  }

  function show(st) {
    if (st.btn.disabled) return;
    if (open && open !== st) close(open);
    sync(st);
    open = st;
    st.list.hidden = false;
    st.btn.setAttribute('aria-expanded', 'true');
    st.field && st.field.classList.add('is-open');
    place(st);
    const cur = st.sel.selectedIndex;
    const ok = enabled(st);
    setActive(st, ok.includes(cur) ? cur : ok[0] ?? -1);
    if (!reduce.matches) {
      st.list.classList.remove('is-in');
      void st.list.offsetWidth;
      st.list.classList.add('is-in');
    }
  }

  function close(st) {
    if (!st) return;
    st.list.hidden = true;
    st.list.classList.remove('is-in');
    st.btn.setAttribute('aria-expanded', 'false');
    st.btn.removeAttribute('aria-activedescendant');
    st.field && st.field.classList.remove('is-open');
    if (open === st) open = null;
  }

  function pick(st, i) {
    if (st.sel.selectedIndex !== i) {
      st.sel.selectedIndex = i;
      st.sel.dispatchEvent(new Event('input', { bubbles: true }));
      st.sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    sync(st);
  }

  function keys(st, e) {
    const isOpen = open === st;
    const ok = enabled(st);
    const pos = ok.indexOf(st.active);
    const move = (to) => { e.preventDefault(); if (!isOpen) show(st); setActive(st, to); };
    switch (e.key) {
      case 'ArrowDown': return isOpen ? move(ok[Math.min(pos + 1, ok.length - 1)]) : (e.preventDefault(), show(st));
      case 'ArrowUp': return isOpen ? move(ok[Math.max(pos - 1, 0)]) : (e.preventDefault(), show(st));
      case 'Home': return isOpen && move(ok[0]);
      case 'End': return isOpen && move(ok[ok.length - 1]);
      case 'PageDown': return isOpen && move(ok[Math.min(pos + 8, ok.length - 1)]);
      case 'PageUp': return isOpen && move(ok[Math.max(pos - 8, 0)]);
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!isOpen) return show(st);
        if (st.active > -1) pick(st, st.active);
        return close(st);
      case 'Escape':
        if (isOpen) { e.preventDefault(); e.stopPropagation(); close(st); }
        return;
      case 'Tab':
        if (isOpen) close(st);
        return;
    }
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const now = Date.now();
      st.typed = (now - st.typedAt > 700 ? '' : st.typed) + e.key.toLowerCase();
      st.typedAt = now;
      const hit = ok.find((i) => st.sel.options[i].textContent.trim().toLowerCase().startsWith(st.typed));
      if (hit === undefined) return;
      e.preventDefault();
      if (isOpen) setActive(st, hit);
      else pick(st, hit);
    }
  }

  let queued = false;
  function syncAll() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      document.querySelectorAll('select.ls-sel-native').forEach((s) => s._lsSel && sync(s._lsSel));
    });
  }

  function scan(root = document) {
    if (!forced && !fine.matches) return;
    root.querySelectorAll(SEL).forEach(enhance);
  }

  function init(force) {
    if (force) forced = true;
    scan();
  }

  document.addEventListener('pointerdown', (e) => {
    if (open && !(open.field || open.btn.parentNode).contains(e.target)) close(open);
  }, true);
  window.addEventListener('resize', () => open && close(open));
  ['click', 'change', 'input', 'keyup', 'reset'].forEach((t) => document.addEventListener(t, syncAll));
  window.addEventListener('popstate', syncAll);
  document.addEventListener('ls-select:sync', syncAll);
  new MutationObserver((recs) => {
    for (const r of recs) for (const n of r.addedNodes) if (n.nodeType === 1) {
      if (n.matches && n.matches(SEL)) { if (forced || fine.matches) enhance(n); }
      else if (n.querySelector) scan(n);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
  fine.addEventListener?.('change', () => scan());

  window.LsSelect = { init, sync: syncAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  setTimeout(syncAll, 600);
})();
